import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { setTitle } from '../utils/environment';
import PageLoading from '../components/PageLoading';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null); // Store return URL in state
  const [isRedirecting, setIsRedirecting] = useState(false); // Prevent re-renders during redirect

  const { login, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse and store return URL on mount (before any navigation can lose it)
  useEffect(() => {
    // Parse return URL from standard query params (BrowserRouter)
    // URL structure: http://localhost:3000/login?return=...
    const searchParams = new URLSearchParams(window.location.search);
    const returnParam = searchParams.get('return');

    if (returnParam) {
      setReturnUrl(returnParam);
    } else {
      // Check location state for internal redirects
      const internalRedirect = location.state?.from?.pathname;
      if (internalRedirect) {
        setReturnUrl(internalRedirect);
      } else {
        setReturnUrl('/dashboard');
      }
    }
  }, []); // Only run once on mount

  // Redirect if already authenticated (only runs on page load, not after login)
  useEffect(() => {
    // Set page title with environment prefix
    setTitle('Sign In · Read It Never');

    // Only redirect if already authenticated when page loads
    // (Don't redirect here after login - handleSubmit handles that)
    if (isAuthenticated && returnUrl) {
      if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
        window.location.replace(returnUrl);
      } else {
        navigate(returnUrl, { replace: true });
      }
    }
  }, []); // Only run on mount, not when isAuthenticated changes

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await login(formData);

      if (result.success) {
        if (!returnUrl) {
          return;
        }

        // If return URL is external (OAuth), redirect to it directly
        if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
          console.log('[OAuth] Full redirect URL:', returnUrl);

          // Set flag to prevent any re-renders
          setIsRedirecting(true);

          // Perform immediate redirect without setTimeout
          window.location.replace(returnUrl);

          // Should never reach here, but just in case
          return;
        } else {
          // Internal navigation
          navigate(returnUrl, { replace: true });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if we're redirecting
  if (isRedirecting) {
    return (
      <PageLoading message="Redirecting to authorization..." />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-clay-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 flex items-center justify-center animate-float">
            <img
              src={new URL('../assets/logo.png', import.meta.url).href}
              alt="Read It Never"
              className="h-20 w-20 rounded-clay-lg shadow-clay"
            />
          </div>
          <h2 className="mt-6 font-display text-3xl font-bold text-heading">
            Welcome back!
          </h2>
          <p className="mt-2 text-lg font-medium text-secondary-text">
            Reading is slow. Just vibe it.
          </p>
          <p className="mt-4 text-sm text-secondary-text">
            Or{' '}
            <Link
              to="/register"
              className="font-semibold text-primary-600 hover:text-primary-500 transition-colors"
            >
              create a new account
            </Link>
          </p>
        </div>

        {/* Form Card */}
        <div className="card-clay-static">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border-2 border-rose-200 dark:border-rose-800 rounded-clay">
                <div className="flex items-center gap-2 text-sm text-rose-700 dark:text-rose-300">
                  <span className="text-lg" role="img" aria-label="error">😅</span>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-body-text mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-clay"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-body-text mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input-clay"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-clay-accent w-full flex justify-center items-center"
              >
                {isSubmitting && (
                  <div className="spinner-fun-sm w-5 h-5 mr-2"></div>
                )}
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            <div className="text-center pt-2">
              <p className="text-xs text-secondary-text">
                By signing in, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
